<template>
  <div class="py-1">
    <MenuOption
      v-if="hasUnregisteredSamples && canUser('orders.registerSamples')"
      id="register-all-samples"
      :title="$t('W: Modtag alle prøver')"
      :isDisabled="isLoading"
      @select="onRegisterAllSamplesSelect">
      <template #icon>
        <IconLucidePackageCheck class="h-4 w-4" />
      </template>
    </MenuOption>
    <MenuOption
      v-if="sampleStatus === 'not-registered' && canUser('orders.registerSamples')"
      id="register"
      :title="$t('W: Modtag prøve')"
      :isDisabled="isLoading"
      @select="onRegisterSelect">
      <template #icon>
        <IconLucidePackageCheck class="h-4 w-4" />
      </template>
    </MenuOption>
    <MenuOption
      v-if="sampleStatus !== 'cancelled' && !isLocked && canUser('orders.editSample')"
      id="edit"
      :title="$t('W: Rediger prøve')"
      :isDisabled="isLoading"
      @select="onEditSampleSelect">
      <template #icon>
        <IconLucidePencil class="h-4 w-4" />
      </template>
    </MenuOption>
    <MenuOption
      v-if="
        sampleStatus === 'registered' &&
        !hasAnyTestStarted &&
        canUser('orders.registerSamples')
      "
      id="unregister"
      :title="$t('W: Annuller modtagelse')"
      :isDisabled="isLoading"
      @select="onUnregisterSelect">
      <template #icon>
        <IconLucideUndo2 class="h-4 w-4" />
      </template>
    </MenuOption>
    <MenuOption
      v-if="showChangeLocation && canUser('orders.changeSampleLocation')"
      id="change-location"
      :title="$t('W: Skift placering')"
      :isDisabled="isLoading"
      @select="onChangeLocationSelect">
      <template #icon>
        <IconLucideMapPin class="h-4 w-4" />
      </template>
    </MenuOption>
    <MenuOption
      v-if="sampleStatus !== 'cancelled' && !isReportLocked && canUser('orders.cancel')"
      id="cancel"
      :title="$t('W: Annuller prøve')"
      :isDisabled="isLoading"
      @select="onCancelSelect">
      <template #icon>
        <IconLucideX class="h-4 w-4" />
      </template>
    </MenuOption>
    <MenuOption
      v-if="sampleStatus === 'cancelled' && !isReportLocked && canUser('orders.cancel')"
      id="uncancel"
      :title="$t('W: Genaktiver prøve')"
      :isDisabled="isLoading"
      @select="onUncancelSelect">
      <template #icon>
        <IconLucideRotateCcw class="h-4 w-4" />
      </template>
    </MenuOption>
  </div>
</template>

<script lang="ts">
import { useLabLocationStore } from '@/stores/labLocationStore'
import { useModalStore } from '@/stores/modalStore'
import { useOrderStore } from '@/stores/orderStore'
import { useUserStore } from '@/stores/userStore'
import type {
  IOrderSampleAnalysis,
  TOrderSampleStatus,
} from '@team-milva/lab-models/resources'
import { mapActions, mapState } from 'pinia'
import { useToast } from '@/globals/typescript/utils/toast'

export default {
  name: 'OrderSampleMenu',

  emits: ['close'],

  props: {
    labAccountID: { type: String, required: true },
    orderID: { type: String, required: true },
    sampleID: { type: String, required: true },
    sampleNumber: { type: Number, required: true },
    sampleStatus: { type: String as () => TOrderSampleStatus, required: true },
    currentLocationID: { type: String as () => string | null, default: null },
    isReportSent: { type: Boolean, default: false },
    analyses: {
      type: Array as () => IOrderSampleAnalysis[],
      default: () => [],
    },
    isLoading: { type: Boolean, default: false },
    isLocked: { type: Boolean, default: false },
    isReportLocked: { type: Boolean, default: false },
    hasUnregisteredSamples: { type: Boolean, required: true },
    orderTatStartDate: { type: Number, default: null },
    dropoffDate: { type: String, default: null },
  },

  computed: {
    ...mapState(useLabLocationStore, ['activeLabLocations']),

    hasAnyTestStarted(): boolean {
      return (this.analyses ?? [])
        .flatMap((analysis) => analysis.analysisTests)
        .some((analysisTest) => analysisTest.timestamps.startedAt !== null)
    },

    showChangeLocation(): boolean {
      if (this.sampleStatus !== 'registered') return false
      if (this.isReportSent) return false
      return this.activeLabLocations.length > 0 || !!this.currentLocationID
    },
  },

  methods: {
    ...mapActions(useModalStore, ['open']),
    ...mapActions(useUserStore, ['canUser']),
    ...mapActions(useOrderStore, [
      'cancelSample',
      'uncancelSample',
      'unregisterSample',
      'registerAllSamples',
      'registerSample',
    ]),

    onRegisterAllSamplesSelect(): void {
      this.$emit('close')
      this.open({
        name: 'ConfirmationModal',
        width: 'sm',
        props: {
          title: this.$t('W: Modtag alle prøver'),
          textLines: [
            this.$t(
              'W: Er du sikker på, at du vil markere alle ikke-modtagne prøver på denne ordre som modtaget?'
            ),
          ],
          onConfirm: async () => {
            try {
              await this.registerAllSamples(this.orderID)
              useToast().success(this.$t('W: Alle prøver modtaget'))
            } catch {
              useToast().error(this.$t('W: Kunne ikke modtage prøver'))
            }
          },
        },
      })
    },

    onRegisterSelect(): void {
      this.$emit('close')
      const isFirstRegistration = this.orderTatStartDate === null
      const today = new Date()
      const todayDateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
      const isDifferentDay =
        isFirstRegistration &&
        !!this.dropoffDate &&
        this.dropoffDate !== todayDateStr

      if (isDifferentDay) {
        this.open({
          name: 'RegisterSampleTatStartDateModal',
          width: 'sm',
          props: {
            onConfirm: async (tatStartDate: number) => {
              try {
                await this.registerSample(this.orderID, this.sampleID, tatStartDate)
                useToast().success(this.$t('W: Prøve modtaget'))
              } catch (err) {
                useToast().error(this.$t('W: Kunne ikke modtage prøve'))
                throw err
              }
            },
          },
        })
        return
      }

      const tatStartDate = isFirstRegistration ? Date.now() : undefined
      this.open({
        name: 'ConfirmationModal',
        width: 'sm',
        props: {
          title: this.$t('W: Modtag prøve'),
          textLines: [
            this.$t(
              'W: Er du sikker på, at du vil markere denne prøve som modtaget?'
            ),
          ],
          onConfirm: async () => {
            try {
              await this.registerSample(this.orderID, this.sampleID, tatStartDate)
              useToast().success(this.$t('W: Prøve modtaget'))
            } catch {
              useToast().error(this.$t('W: Kunne ikke modtage prøve'))
            }
          },
        },
      })
    },

    onEditSampleSelect(): void {

      this.$emit('close')
      this.open({
        name: 'EditSampleModal',
        width: 'md',
        props: {
          labAccountID: this.labAccountID,
          orderID: this.orderID,
          sampleID: this.sampleID,
          sampleNumber: this.sampleNumber,
        },
      })
    },

    onUnregisterSelect(): void {
      this.$emit('close')
      this.open({
        name: 'ConfirmationModal',
        width: 'sm',
        props: {
          title: this.$t('W: Annuller modtagelse'),
          textLines: [
            this.$t(
              'W: Er du sikker på, at du vil annullere modtagelsen af denne prøve?'
            ),
          ],
          onConfirm: async () => {
            try {
              await this.unregisterSample(this.orderID, this.sampleID)
              useToast().success(this.$t('W: Modtagelse annulleret'))
            } catch {
              useToast().error(this.$t('W: Kunne ikke annullere modtagelse'))
            }
          },
        },
      })
    },

    onChangeLocationSelect(): void {
      this.$emit('close')
      this.open({
        name: 'ChangeSampleLocationModal',
        width: 'sm',
        props: {
          orderID: this.orderID,
          sampleID: this.sampleID,
          currentLocationID: this.currentLocationID,
        },
      })
    },

    onCancelSelect(): void {
      this.$emit('close')
      this.open({
        name: 'ConfirmationModal',
        width: 'sm',
        props: {
          title: this.$t('W: Annuller prøve'),
          textLines: [
            this.$t('W: Er du sikker på, at du vil annullere denne prøve?'),
            this.$t('W: Alle analyser på prøven vil også blive annulleret.'),
          ],
          onConfirm: async () => {
            try {
              await this.cancelSample(this.orderID, this.sampleID)
              useToast().success(this.$t('W: Prøve annulleret'))
            } catch {
              useToast().error(this.$t('W: Kunne ikke annullere prøve'))
            }
          },
        },
      })
    },

    onUncancelSelect(): void {
      this.$emit('close')
      this.open({
        name: 'ConfirmationModal',
        width: 'sm',
        props: {
          title: this.$t('W: Genaktiver prøve'),
          textLines: [
            this.$t('W: Er du sikker på, at du vil genaktivere denne prøve?'),
            this.$t('W: Alle analyser på prøven vil også blive genaktiveret.'),
          ],
          onConfirm: async () => {
            try {
              await this.uncancelSample(this.orderID, this.sampleID)
              useToast().success(this.$t('W: Prøve genaktiveret'))
            } catch {
              useToast().error(this.$t('W: Kunne ikke genaktivere prøve'))
            }
          },
        },
      })
    },
  },
}
</script>

